import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ArrowIcon from "../assets/icons/arrow.svg";
import { useAuth } from "../context/auth-context";

/** --- Resolver de avatar para CRA/Webpack (require.context) --- */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

/** Hash simples e estável (string -> 0..5) */
function hashIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % 6;
}

/** Tenta resolver ./<id>.jpg/.jpeg/.png; se não achar, usa profile-<1..6>.* */
export function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];
    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1; // 1..6
  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];
  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  for (let i = 1; i <= 6; i++) {
    try {
      return profileCtx(`./profile-${i}.jpg`) as string;
    } catch {}
  }
  return undefined;
}

/** Iniciais como fallback textual (se até a imagem padrão falhar) */
export function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase();
}

/** ======= Conectividade: config do ping ======= */
const API_PING_URL = "https://www.google.com.br/"; // ping leve (HEAD)
const API_PING_INTERVAL_MS = 3000;
const API_PING_TIMEOUT_MS = 4000;

/* =======================================================================================
   HeaderApi
   ======================================================================================= */

const HeaderApi: React.FC = () => {
  const { sessao, logout } = useAuth();
  const navigate = useNavigate();

  const nome = sessao?.nome || "";
  const departamento = sessao?.departamento || "";
  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const [liveUrl, setLiveUrl] = useState<string | undefined>(undefined);
  const [companyMenuOpen, setCompanyMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // ===== Conectividade =====
  const [netOnline, setNetOnline] = useState<boolean>(navigator.onLine);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);
  const [lastPingAt, setLastPingAt] = useState<number | null>(null);

  // ===== API key (access_token) =====
  const [apiKeyDisplay, setApiKeyDisplay] = useState<string>("");

  // Carrega o access_token do localStorage ao montar
  useEffect(() => {
    try {
      const stored = localStorage.getItem("access_token");
      if (stored) {
        setApiKeyDisplay(stored);
      } else {
        setApiKeyDisplay("");
      }
    } catch {
      setApiKeyDisplay("");
    }
  }, []);

  // ===== Copiar API key =====
  const [copiedApiKey, setCopiedApiKey] = useState(false);

  const handleCopyApiKey = useCallback(async () => {
    if (!apiKeyDisplay) return; // nada para copiar
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(apiKeyDisplay);
      } else {
        // fallback tosco, mas evita quebrar
        const temp = document.createElement("textarea");
        temp.value = apiKeyDisplay;
        temp.style.position = "fixed";
        temp.style.left = "-9999px";
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
      }
      setCopiedApiKey(true);
      setTimeout(() => setCopiedApiKey(false), 1200);
    } catch {
      // se falhar, só ignora (poderia colocar um toast futuramente)
    }
  }, [apiKeyDisplay]);

  const pingApi = useCallback(async () => {
    if (!navigator.onLine) {
      setApiReachable(false);
      setLastPingAt(Date.now());
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), API_PING_TIMEOUT_MS);
    try {
      await fetch(API_PING_URL, {
        method: "HEAD",
        cache: "no-store",
        mode: "no-cors",
        signal: controller.signal,
      });
      setApiReachable(true);
    } catch {
      setApiReachable(false);
    } finally {
      clearTimeout(t);
      setLastPingAt(Date.now());
    }
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setNetOnline(true);
      pingApi();
    };
    const onOffline = () => setNetOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    pingApi();

    const onVis = () => {
      if (document.visibilityState === "visible") pingApi();
    };
    document.addEventListener("visibilitychange", onVis);

    const id = setInterval(pingApi, API_PING_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, [pingApi]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  // ==== LOGOUT COMPLETO (HeaderApi) ====
  const doLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setMenuOpen(false);
    setCompanyMenuOpen(false);

    try {
      // 1) avisa backend para invalidar sessão/cookie
      try {
        await fetch("https://api.eva-googlemarine.com/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // mesmo se der erro, continua o fluxo local
      }

      // 2) limpa accessToken do localStorage (usado pelas APIs)
      try {
        localStorage.removeItem("access_token");
      } catch {}

      // 3) limpa sessão no contexto React
      await logout();
    } finally {
      setLoggingOut(false);
      // 4) redireciona para a tela de login
      window.location.replace("/");
    }
  };

  const profileUrl = useMemo(() => {
    if (liveUrl) return liveUrl;
    const local = resolveProfileUrl(pictureId, nome);
    return local || undefined;
  }, [liveUrl, pictureId, nome]);

  const hasName = !!nome;

  const netLabel = useMemo(() => {
    if (!netOnline) return "Sem internet";
    if (apiReachable === true) return "Conectado (API ok)";
    if (apiReachable === false) return "Internet ok, API indisponível";
    return "Verificando conexão…";
  }, [netOnline, apiReachable]);

  const netColor = useMemo(() => {
    if (!netOnline) return "#ef4444";
    if (apiReachable === true) return "#10b981";
    if (apiReachable === false) return "#f59e0b";
    return "#9ca3af";
  }, [netOnline, apiReachable]);

  const dep = (departamento || "").toLowerCase();
  const isDev = dep === "desenvolvedor";

  // handler para o badge de API Doc
  const handleGoApiDoc = () => {
    navigate("/entrar");
  };

  return (
    <motion.header
      style={styles.container as any}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <div style={styles.topHairline} />

      {/* ESQUERDA: API key clicável (access_token do localStorage) */}
      <button
        type="button"
        onClick={handleCopyApiKey}
        style={{
          ...styles.apiKeyBadge,
          borderColor: copiedApiKey ? "#10b981" : "rgba(31,41,55,0.35)",
          color: copiedApiKey ? "#065f46" : "#1f2937",
        }}
        title={
          !apiKeyDisplay
            ? "Nenhum access_token encontrado no navegador"
            : copiedApiKey
            ? "Copiado!"
            : "Clique para copiar"
        }
        disabled={!apiKeyDisplay}
      >
        <span style={{ opacity: 0.85 }}>Access Token:</span>{" "}
        <span style={styles.apiKeyValue}>{apiKeyDisplay || "—"}</span>
        {copiedApiKey && <span style={styles.apiKeyCopiedHint}>copiado</span>}
      </button>

      {/* DIREITA: badges dev + usuário/menu/conectividade */}
      <div style={styles.userInfo} ref={menuRef}>
        {/* Badges DEV à esquerda do avatar */}
        <AnimatePresence>
          {isDev && (
            <>
              {/* Badge azul de API DOC */}
              <motion.button
                key="dev-api-doc-badge"
                type="button"
                style={styles.devApiBadge as React.CSSProperties}
                onClick={handleGoApiDoc}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Clique para Retornar ao Sistema</span>
              </motion.button>
            </>
          )}
        </AnimatePresence>

        <motion.div
          style={{ position: "relative", display: "inline-block" }}
          whileHover={{ y: -1 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
        >
          <AnimatePresence initial={false} mode="wait">
            {profileUrl ? (
              <motion.img
                key="avatar-img"
                src={profileUrl}
                alt={nome ? `Foto de ${nome}` : "Foto do usuário"}
                style={styles.avatar as any}
                onClick={() => setMenuOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMenuOpen((v) => !v);
                  }
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                loading="lazy"
              />
            ) : (
              <motion.div
                key="avatar-fallback"
                style={{ ...styles.avatar, ...styles.avatarFallback }}
                onClick={() => setMenuOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMenuOpen((v) => !v);
                  }
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
              >
                {hasName ? getInitials(nome) : ""}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.span
            key={nome || "nome-loading"}
            style={styles.name}
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.2 }}
          >
            {nome || "—"}
          </motion.span>
        </AnimatePresence>

        <div
          style={{ ...styles.netIndicator, backgroundColor: netColor }}
          title={
            lastPingAt
              ? `${netLabel} • Atualizado há ${Math.max(
                  0,
                  Math.floor((Date.now() - lastPingAt) / 1000)
                )}s`
              : netLabel
          }
          aria-label={netLabel}
        />

        <motion.img
          src={ArrowIcon}
          alt="Abrir menu"
          style={{ ...styles.arrow, cursor: "pointer" }}
          onClick={() => {
            setMenuOpen((v) => {
              const next = !v;
              if (!next) setCompanyMenuOpen(false);
              return next;
            });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setMenuOpen((v) => {
                const next = !v;
                if (!next) setCompanyMenuOpen(false);
                return next;
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          animate={{ rotate: menuOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
        />

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              {/* ====== Selecionar empresa (apenas DEV) ====== */}
              {isDev && (
                <motion.button
                  type="button"
                  style={styles.menuItem}
                  onClick={() => setCompanyMenuOpen((prev) => !prev)}
                  whileHover={{ backgroundColor: "rgba(18,66,128,.06)" }}
                  whileTap={{ scale: 0.995 }}
                >
                  <span style={{ marginRight: 8 }}>←</span>
                  Selecionar empresa
                </motion.button>
              )}

              <motion.button
                type="button"
                style={styles.menuItem}
                onClick={doLogout}
                disabled={loggingOut}
                whileHover={{ backgroundColor: "rgba(18,66,128,.06)" }}
                whileTap={{ scale: 0.995 }}
              >
                {loggingOut ? "Saindo..." : "Sair"}
              </motion.button>

              {/* ====== Submenu de empresas (abre à esquerda) ====== */}
              {isDev && companyMenuOpen && (
                <motion.div
                  style={styles.companyMenu}
                  initial={{ opacity: 0, x: 6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                >
                  <div style={styles.companyMenuTitle}>Empresas</div>
                  <button type="button" style={styles.companyMenuItem}>
                    Havox
                  </button>
                  <button type="button" style={styles.companyMenuItem}>
                    Googlemarine
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "54px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: "50px",
    paddingLeft: "25px",
    boxSizing: "border-box",
    zIndex: 1000,
  },
  topHairline: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background:
      "linear-gradient(90deg, rgba(18,66,128,0.22), rgba(18,66,128,0.02) 60%, rgba(18,66,128,0))",
    pointerEvents: "none",
  },
  // badge da API key (lado esquerdo)
  apiKeyBadge: {
    marginRight: "auto",
    padding: "4px 10px",
    borderRadius: 1,
    border: "1px solid rgba(31,41,55,0.35)",
    fontSize: 11,
    fontFamily: "'Poppins', sans-serif",
    background: "rgba(243,244,246,0.9)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    outline: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    whiteSpace: "nowrap",
  },
  apiKeyValue: {
    fontFamily: "monospace",
    fontSize: 11,
    padding: "1px 4px",
    borderRadius: 4,
    background: "rgba(17,24,39,0.06)",
  },
  apiKeyCopiedHint: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 0.08,
    marginLeft: 4,
    opacity: 0.9,
  },
  userInfo: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "9px",
    marginLeft: "auto",
  },
  avatar: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    objectFit: "cover",
    overflow: "hidden",
    display: "inline-block",
    backgroundColor: "#ccd2db",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  avatarFallback: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "26px",
    textAlign: "center",
    userSelect: "none",
    background:
      "linear-gradient(135deg, rgba(18,66,128,0.85), rgba(18,66,128,0.65))",
  },
  devBadge: {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    fontFamily: "'Poppins', sans-serif",
    background:
      "linear-gradient(135deg, rgba(245,158,11,0.16), rgba(245,158,11,0.06))",
    border: "1px solid rgba(245,158,11,0.8)",
    color: "#92400e",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.02)",
  },
  devApiBadge: {
    padding: "2px 17px",
    borderRadius: 1,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    fontFamily: "'Poppins', sans-serif",
    background:
      "linear-gradient(135deg, rgba(31,31,31,0.18), rgba(31,31,31,0.06))",
    border: "1px solid rgba(31,31,31,0.9)",
    color: "#4D525FFF",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.02)",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginRight: 10,
    cursor: "pointer",
  },
  name: {
    marginLeft: "0px",
    marginRight: "9px",
    fontSize: "12px",
    fontWeight: 300,
    fontFamily: "'Poppins', sans-serif",
    color: "#2b2f33",
  },
  netIndicator: {
    width: 8,
    height: 8,
    borderRadius: 999,
    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
  },
  arrow: {
    width: "12px",
    height: "12px",
    filter: "invert(1)",
  },
  menu: {
    position: "absolute",
    top: "36px",
    right: 0,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "8px",
    boxShadow: "0 12px 28px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.04)",
    minWidth: "180px",
    padding: "6px",
    zIndex: 1100,
  },
  menuItem: {
    width: "100%",
    background: "transparent",
    border: "none",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "Arial",
    fontSize: "13px",
    fontWeight: 300,
    color: "#1f2937",
  },
  companyMenu: {
    position: "absolute",
    top: 0,
    right: "100%",
    marginRight: 8,
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.06)",
    borderRadius: "8px",
    boxShadow: "0 12px 28px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.04)",
    minWidth: "180px",
    padding: "8px 8px 6px 8px",
    zIndex: 1099,
  },
  companyMenuTitle: {
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "'Poppins', sans-serif",
    color: "#374151",
    marginBottom: 6,
    opacity: 0.8,
  },
  companyMenuItem: {
    width: "100%",
    background: "transparent",
    border: "none",
    textAlign: "left",
    padding: "8px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "Arial",
    fontSize: "13px",
    fontWeight: 300,
    color: "#1f2937",
  },
};

export default HeaderApi;
